import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Row,
  Col,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  FormGroup,
  Label,
  FormFeedback,
} from "reactstrap";
import { ClipLoader } from "react-spinners";
import { FaMapMarkedAlt, FaRegClock } from "react-icons/fa";
import fileDownload from "js-file-download";
import Select from "react-select";
import AsyncSelect from "react-select/async";

import api from "../../../services/api";
import { useToast } from "../../../hooks/toast";
import useDebounce from "../../../hooks/useDebounce";
import FileUpload from "../../../components/Input/fileUpload";
import { useCompanies } from "hooks/companies";
import SpecificEmpresaRequiredNotice from "../../../components/SpecificEmpresaRequiredNotice";
import PageHero, {
  PageHeroExportButton,
  PageHeroRefreshButton,
} from "../../../components/PageHero";
import FilterBand from "../../../components/FilterBand";
import MetricCard from "../../../components/MetricCard";
import MetricCardGroup from "../../../components/MetricCardGroup";
import ShellCard from "../../../components/ShellCard";
import { AUTOPILOT_COLORS } from "../../../components/autopilot/styles";
import { reactSelectStyles, reactSelectPortalProps } from "../../../utils/reactSelectStyles";
import "./Holidays.css";

const ALL_FILTER = "__ALL__";
const urlGet = "/embarcadores/feriados";
const urlPostCreateManualHoliday = "/embarcadores/feriados";
const urlGetExportCsv = "/embarcadores/feriados/export-csv";
const urlPostImportCsv = "/embarcadores/feriados/import-csv";
const urlPostSyncHolidays = "/embarcadores/feriados/sincronizar";
const HOLIDAYS_DEBUG = process.env.NODE_ENV !== "production";
const DEFAULT_SYNC_SCOPES = ["nacional", "estadual"];

const TYPE_META = {
  nacional: { value: "nacional", label: "Nacional", tone: "info" },
  estadual: { value: "estadual", label: "Estadual", tone: "success" },
  municipal: { value: "municipal", label: "Municipal", tone: "warning" },
  outro: { value: "outro", label: "Outro", tone: "muted" },
};

const SYNC_SCOPE_OPTIONS = [
  {
    value: "nacional",
    label: "Nacionais",
    helper: "Busca a base nacional automatica do ano selecionado.",
  },
  {
    value: "estadual",
    label: "Estaduais",
    helper: "Usa a UF do cadastro do embarcador para sincronizar o estado.",
  },
  {
    value: "capitais",
    label: "Capitais",
    helper: "Sincroniza os feriados municipais das 27 capitais suportadas pela API.",
  },
];

const VIEW_META = {
  upcoming: {
    value: "upcoming",
    label: "Proximos",
    title: "Proximos feriados",
    helper: "Hoje e datas futuras dentro do recorte atual",
    iconClassName: "tim-icons icon-calendar-60",
    tone: "info",
  },
  past: {
    value: "past",
    label: "Passados",
    title: "Feriados passados",
    helper: "Datas anteriores ao dia de hoje dentro do recorte atual",
    iconClassName: "tim-icons icon-watch-time",
    tone: "warning",
  },
  active: {
    value: "active",
    label: "Ativos",
    title: "Feriados ativos",
    helper: "Registros habilitados na base, independentemente da data",
    iconClassName: "tim-icons icon-check-2",
    tone: "success",
  },
  inactive: {
    value: "inactive",
    label: "Inativos",
    title: "Feriados inativos",
    helper: "Registros desativados na base, independentemente da data",
    iconClassName: "tim-icons icon-simple-remove",
    tone: "danger",
  },
};

const longDateFormatter = new Intl.DateTimeFormat("pt-BR", {
  day: "2-digit",
  month: "long",
  year: "numeric",
});

const dayFormatter = new Intl.DateTimeFormat("pt-BR", {
  day: "2-digit",
});

const monthShortFormatter = new Intl.DateTimeFormat("pt-BR", {
  month: "short",
});

const monthFormatter = new Intl.DateTimeFormat("pt-BR", {
  month: "long",
  year: "numeric",
});

function createValidDate(year, month, day) {
  const candidate = new Date(year, month - 1, day);

  if (
    candidate.getFullYear() !== year ||
    candidate.getMonth() !== month - 1 ||
    candidate.getDate() !== day
  ) {
    return null;
  }

  return candidate;
}

function startOfDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function normalizeText(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function capitalizeLabel(value) {
  if (!value) {
    return "";
  }

  return value.charAt(0).toUpperCase() + value.slice(1);
}

function parseHolidayDate(rawValue) {
  if (!rawValue) {
    return null;
  }

  if (rawValue instanceof Date && !Number.isNaN(rawValue.getTime())) {
    return startOfDay(rawValue);
  }

  const value = String(rawValue).trim();
  let match = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (match) {
    return createValidDate(Number(match[3]), Number(match[2]), Number(match[1]));
  }

  match = value.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (match) {
    return createValidDate(Number(match[3]), Number(match[2]), Number(match[1]));
  }

  match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    return createValidDate(Number(match[1]), Number(match[2]), Number(match[3]));
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  return startOfDay(parsedDate);
}

function extractHolidayYear(rawValue) {
  if (!rawValue) {
    return null;
  }

  if (rawValue instanceof Date && !Number.isNaN(rawValue.getTime())) {
    return String(rawValue.getFullYear());
  }

  const value = String(rawValue).trim();
  let match = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (match) {
    return match[3];
  }

  match = value.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (match) {
    return match[3];
  }

  match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    return match[1];
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  return String(parsedDate.getFullYear());
}

function getTypeMeta(rawType) {
  const normalizedType = normalizeText(rawType);

  if (normalizedType.includes("nac")) {
    return TYPE_META.nacional;
  }

  if (normalizedType.includes("estad")) {
    return TYPE_META.estadual;
  }

  if (normalizedType.includes("mun")) {
    return TYPE_META.municipal;
  }

  if (!normalizedType) {
    return TYPE_META.outro;
  }

  return {
    value: normalizedType,
    label: rawType,
    tone: TYPE_META.outro.tone,
  };
}

function getStatusMeta(statusValue) {
  if (typeof statusValue === "string") {
    const normalizedStatus = normalizeText(statusValue);
    if (["false", "0", "inativo", "nao", "no"].includes(normalizedStatus)) {
      return { isActive: false, label: "Inativo", tone: "danger" };
    }
    if (["true", "1", "ativo", "sim", "yes"].includes(normalizedStatus)) {
      return { isActive: true, label: "Ativo", tone: "success" };
    }
  }

  return statusValue
    ? { isActive: true, label: "Ativo", tone: "success" }
    : { isActive: false, label: "Inativo", tone: "danger" };
}

function getDistanceInDays(targetDate, baseDate) {
  const millisecondsPerDay = 24 * 60 * 60 * 1000;

  return Math.round(
    (startOfDay(targetDate).getTime() - startOfDay(baseDate).getTime()) /
      millisecondsPerDay
  );
}

function formatRelativeDays(days) {
  if (days === null || days === undefined) {
    return "Sem data";
  }

  if (days === 0) {
    return "Hoje";
  }

  if (days === 1) {
    return "Amanha";
  }

  if (days === -1) {
    return "Ontem";
  }

  if (days > 1) {
    return `Em ${days} dias`;
  }

  return `Ha ${Math.abs(days)} dias`;
}

function formatLocationLabel(typeMeta, city, state) {
  if (typeMeta.value === TYPE_META.nacional.value) {
    return "Brasil";
  }

  const locationParts = [city, state].filter(Boolean);
  if (locationParts.length > 0) {
    return locationParts.join(", ");
  }

  if (state) {
    return state;
  }

  return "Abrangencia nao informada";
}

function normalizeHoliday(item, index, today) {
  const rawDateValue = item?.data || item?.Data;
  const parsedDate = parseHolidayDate(rawDateValue);
  const typeMeta = getTypeMeta(item?.tipo || item?.Tipo);
  const statusMeta = getStatusMeta(item?.status ?? item?.Status);
  const state = item?.estado || item?.Estado || "";
  const city = item?.cidade || item?.Cidade || "";
  const name = item?.nome || item?.Nome || "Feriado sem nome";
  const description = item?.descricao || item?.Descricao || "";
  const year = extractHolidayYear(rawDateValue) || (parsedDate ? String(parsedDate.getFullYear()) : "Sem data");
  const monthKey = parsedDate
    ? `${parsedDate.getFullYear()}-${String(parsedDate.getMonth() + 1).padStart(
        2,
        "0"
      )}`
    : "sem-data";
  const distanceInDays = parsedDate ? getDistanceInDays(parsedDate, today) : null;
  const locationLabel = formatLocationLabel(typeMeta, city, state);

  return {
    id: `${index}-${name}-${item?.data || item?.Data || "sem-data"}`,
    raw: item,
    parsedDate,
    dateLabel: parsedDate
      ? longDateFormatter.format(parsedDate)
      : item?.data || item?.Data || "Sem data",
    dayLabel: parsedDate ? dayFormatter.format(parsedDate) : "--",
    monthShortLabel: parsedDate ? monthShortFormatter.format(parsedDate) : "",
    monthKey,
    monthLabel: parsedDate ? capitalizeLabel(monthFormatter.format(parsedDate)) : "Sem data definida",
    year,
    sortTime: parsedDate ? parsedDate.getTime() : Number.MAX_SAFE_INTEGER,
    distanceInDays,
    relativeLabel: formatRelativeDays(distanceInDays),
    name,
    description,
    state,
    city,
    locationLabel,
    typeMeta,
    statusMeta,
    searchIndex: normalizeText(
      [
        name,
        description,
        state,
        city,
        typeMeta.label,
        statusMeta.label,
        parsedDate ? longDateFormatter.format(parsedDate) : item?.data || item?.Data,
      ]
        .filter(Boolean)
        .join(" ")
    ),
  };
}

function compareByOperationalRelevance(a, b) {
  const aHasDate = Boolean(a.parsedDate);
  const bHasDate = Boolean(b.parsedDate);

  if (aHasDate !== bHasDate) {
    return aHasDate ? -1 : 1;
  }

  if (!aHasDate && !bHasDate) {
    return a.name.localeCompare(b.name, "pt-BR");
  }

  const aIsPast = a.distanceInDays < 0;
  const bIsPast = b.distanceInDays < 0;

  if (aIsPast !== bIsPast) {
    return aIsPast ? 1 : -1;
  }

  if (!aIsPast && !bIsPast) {
    return a.sortTime - b.sortTime;
  }

  return b.sortTime - a.sortTime;
}

function getCompanyLabel(empresa) {
  return (
    empresa?.label ||
    empresa?.nome ||
    empresa?.Nome ||
    empresa?.name ||
    "Embarcador selecionado"
  );
}

function buildFilename(value) {
  const normalized = normalizeText(value)
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

  return normalized || "embarcador";
}

function getHolidayLoadErrorMessage(error) {
  return normalizeText(error?.response?.data?.error || error?.message);
}

function createEmptyManualHolidayForm() {
  return {
    nome: "",
    data: "",
    tipo: TYPE_META.estadual.value,
    status: "active",
    uf: "",
    cidade: null,
    descricao: "",
  };
}

const fieldLabelStyle = {
  color: AUTOPILOT_COLORS.muted,
  fontSize: "0.76rem",
  marginBottom: "0.35rem",
};

const loadErrorStyle = {
  marginTop: "0.75rem",
  color: AUTOPILOT_COLORS.statusDanger,
  fontSize: "0.86rem",
};

function findOption(options, value) {
  return options.find((option) => option.value === value) || options[0];
}

function MetaPill({ tone, children }) {
  return <span className={`holidays-pill holidays-pill--${tone}`}>{children}</span>;
}

const Holidays = () => {
  const [data, setData] = useState([]);
  const [isImportModalOpen, setImportModalOpen] = useState(false);
  const [isNationalSyncModalOpen, setNationalSyncModalOpen] = useState(false);
  const [isManualModalOpen, setManualModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [syncingNational, setSyncingNational] = useState(false);
  const [savingManualHoliday, setSavingManualHoliday] = useState(false);
  const [loadingManualStates, setLoadingManualStates] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [yearFilter, setYearFilter] = useState(ALL_FILTER);
  const [typeFilter, setTypeFilter] = useState(ALL_FILTER);
  const [stateFilter, setStateFilter] = useState(ALL_FILTER);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [viewFilter, setViewFilter] = useState(VIEW_META.upcoming.value);
  const [manualStateOptions, setManualStateOptions] = useState([]);
  const [manualHolidayForm, setManualHolidayForm] = useState(createEmptyManualHolidayForm);
  const [manualHolidayErrors, setManualHolidayErrors] = useState({});
  const { addToast } = useToast();
  const { empresa, hasEmpresaSelection } = useCompanies();
  const empresaId = empresa?.value;
  const empresaLabel = getCompanyLabel(empresa);
  const today = useMemo(() => startOfDay(new Date()), []);
  const currentYear = String(today.getFullYear());
  const previousYear = String(today.getFullYear() - 1);
  const nextYear = String(today.getFullYear() + 1);
  const [nationalSyncYear, setNationalSyncYear] = useState(String(today.getFullYear()));
  const [nationalSyncScopes, setNationalSyncScopes] = useState(DEFAULT_SYNC_SCOPES);
  const debouncedSearch = useDebounce(searchTerm, 250);

  const loadData = useCallback(async () => {
    if (!hasEmpresaSelection) {
      setData([]);
      setLoadError("");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setLoadError("");
      const result = await api.get(urlGet, {
        params: { idEmbarcador: empresaId },
      });

      if (HOLIDAYS_DEBUG) {
        console.log("[Holidays] API payload", {
          empresaId,
          status: result.status,
          total: Array.isArray(result.data) ? result.data.length : 0,
          payload: result.data,
        });
      }

      if (result.status === 200) {
        setData(Array.isArray(result.data) ? result.data : []);
      } else {
        setData([]);
      }
    } catch (error) {
      setData([]);
      const errorMessage = getHolidayLoadErrorMessage(error);

      if (errorMessage.includes("nenhum feriado encontrado")) {
        setLoadError("");
        return;
      }

      setLoadError("Nao foi possivel carregar os feriados deste embarcador.");
      addToast({
        place: "tr",
        type: "danger",
        title: "Erro ao carregar",
        description: "Nao foi possivel buscar os feriados do embarcador selecionado.",
        icon: "tim-icons icon-alert-circle-exc",
      });
    } finally {
      setLoading(false);
    }
  }, [addToast, empresaId, hasEmpresaSelection]);

  useEffect(() => {
    if (!hasEmpresaSelection) {
      setData([]);
      setLoadError("");
      setLoading(false);
      return;
    }

    setData([]);
    loadData();
  }, [empresaId, hasEmpresaSelection, loadData]);

  const handleDownload = useCallback(async () => {
    try {
      setDownloading(true);
      const result = await api.get(urlGetExportCsv, {
        params: { idEmbarcador: empresaId },
        responseType: "blob",
      });

      if (result.status === 200) {
        fileDownload(result.data, `feriados-${buildFilename(empresaLabel)}.csv`);
      }
    } catch (error) {
      addToast({
        place: "tr",
        type: "danger",
        title: "Erro ao exportar",
        description: "Nao foi possivel baixar o CSV de feriados agora.",
        icon: "tim-icons icon-alert-circle-exc",
      });
    } finally {
      setDownloading(false);
    }
  }, [addToast, empresaId, empresaLabel]);

  const handleImportSuccess = useCallback(() => {
    setImportModalOpen(false);
    loadData();
  }, [loadData]);

  const closeManualModal = useCallback(() => {
    setManualModalOpen(false);
    setManualHolidayErrors({});
  }, []);

  const openManualModal = useCallback(() => {
    setManualHolidayForm(createEmptyManualHolidayForm());
    setManualHolidayErrors({});
    setManualModalOpen(true);
  }, []);

  useEffect(() => {
    if (!isManualModalOpen || manualStateOptions.length > 0) {
      return;
    }

    let mounted = true;

    (async () => {
      try {
        setLoadingManualStates(true);
        const result = await api.get("/frete/estados");
        if (!mounted) {
          return;
        }

        const options = (Array.isArray(result.data) ? result.data : []).map((item) => ({
          value: item.uf,
          label: `${item.uf} • ${item.nome}`,
          idEstado: item.id,
        }));
        setManualStateOptions(options);
      } catch (error) {
        if (mounted) {
          addToast({
            place: "tr",
            type: "danger",
            title: "Erro ao carregar UFs",
            description: "Nao foi possivel carregar a lista de estados para o cadastro manual.",
            icon: "tim-icons icon-alert-circle-exc",
          });
        }
      } finally {
        if (mounted) {
          setLoadingManualStates(false);
        }
      }
    })();

    return () => {
      mounted = false;
    };
  }, [addToast, isManualModalOpen, manualStateOptions.length]);

  const manualTypeRequiresState =
    manualHolidayForm.tipo === TYPE_META.estadual.value ||
    manualHolidayForm.tipo === TYPE_META.municipal.value;
  const manualTypeRequiresCity = manualHolidayForm.tipo === TYPE_META.municipal.value;

  const updateManualHolidayForm = useCallback((field, value) => {
    setManualHolidayForm((current) => {
      const next = {
        ...current,
        [field]: value,
      };

      if (field === "tipo") {
        if (value === TYPE_META.nacional.value) {
          next.uf = "";
          next.cidade = null;
        } else if (value !== TYPE_META.municipal.value) {
          next.cidade = null;
        }
      }

      if (field === "uf") {
        next.cidade = null;
      }

      return next;
    });

    setManualHolidayErrors((current) => {
      if (!current[field] && !(field === "uf" && current.idCidade) && !(field === "tipo")) {
        return current;
      }

      const next = { ...current };
      delete next[field];

      if (field === "tipo") {
        delete next.uf;
        delete next.idCidade;
      }

      if (field === "uf") {
        delete next.idCidade;
      }

      return next;
    });
  }, []);

  const loadManualCityOptions = useCallback(
    async (inputValue) => {
      if (!manualTypeRequiresCity || !manualHolidayForm.uf) {
        return [];
      }

      try {
        const result = await api.get("/cidades", {
          params: {
            uf: manualHolidayForm.uf,
            q: inputValue || "",
            limit: 20,
          },
        });

        return (Array.isArray(result.data) ? result.data : []).map((item) => ({
          value: item.id,
          label: item.nome,
          uf: item.uf,
        }));
      } catch (error) {
        return [];
      }
    },
    [manualHolidayForm.uf, manualTypeRequiresCity]
  );

  const validateManualHolidayForm = useCallback(() => {
    const errors = {};

    if (!manualHolidayForm.nome.trim()) {
      errors.nome = "Informe o nome do feriado.";
    }

    if (!manualHolidayForm.data) {
      errors.data = "Informe a data do feriado.";
    }

    if (manualTypeRequiresState && !manualHolidayForm.uf) {
      errors.uf = "Selecione a UF.";
    }

    if (manualTypeRequiresCity && !manualHolidayForm.cidade) {
      errors.idCidade = "Selecione a cidade.";
    }

    return errors;
  }, [manualHolidayForm, manualTypeRequiresCity, manualTypeRequiresState]);

  const handleCreateManualHoliday = useCallback(async () => {
    const nextErrors = validateManualHolidayForm();

    if (Object.keys(nextErrors).length > 0) {
      setManualHolidayErrors(nextErrors);
      return;
    }

    try {
      setSavingManualHoliday(true);
      await api.post(urlPostCreateManualHoliday, {
        idEmbarcador: empresaId,
        nome: manualHolidayForm.nome.trim(),
        data: manualHolidayForm.data,
        tipo: manualHolidayForm.tipo,
        status: manualHolidayForm.status === "active",
        uf: manualTypeRequiresState ? manualHolidayForm.uf : undefined,
        idCidade: manualTypeRequiresCity ? manualHolidayForm.cidade?.value : undefined,
        descricao: manualHolidayForm.descricao.trim(),
      });

      addToast({
        place: "tr",
        type: "success",
        title: "Feriado cadastrado",
        description: "O feriado foi salvo manualmente na base do embarcador.",
        icon: "tim-icons icon-check-2",
      });

      closeManualModal();
      loadData();
    } catch (error) {
      addToast({
        place: "tr",
        type: "danger",
        title: "Erro ao cadastrar",
        description:
          error?.response?.data?.error || "Nao foi possivel cadastrar o feriado manualmente.",
        icon: "tim-icons icon-alert-circle-exc",
      });
    } finally {
      setSavingManualHoliday(false);
    }
  }, [
    addToast,
    closeManualModal,
    empresaId,
    loadData,
    manualHolidayForm,
    manualTypeRequiresCity,
    manualTypeRequiresState,
    validateManualHolidayForm,
  ]);

  const handleOpenNationalSyncModal = useCallback(() => {
    setNationalSyncYear(yearFilter !== ALL_FILTER ? yearFilter : currentYear);
    setNationalSyncScopes((current) =>
      current.length > 0 ? current : [...DEFAULT_SYNC_SCOPES]
    );
    setNationalSyncModalOpen(true);
  }, [currentYear, yearFilter]);

  const toggleNationalSyncScope = useCallback((scope) => {
    setNationalSyncScopes((current) =>
      current.includes(scope) ? current.filter((item) => item !== scope) : [...current, scope]
    );
  }, []);

  const handleSyncNationalHolidays = useCallback(async () => {
    const parsedYear = Number(nationalSyncYear);

    if (!Number.isInteger(parsedYear) || parsedYear < 2000 || parsedYear > 2100) {
      addToast({
        place: "tr",
        type: "danger",
        title: "Ano invalido",
        description: "Informe um ano valido entre 2000 e 2100 para sincronizar os feriados.",
        icon: "tim-icons icon-alert-circle-exc",
      });
      return;
    }

    if (nationalSyncScopes.length === 0) {
      addToast({
        place: "tr",
        type: "danger",
        title: "Selecione um escopo",
        description: "Escolha pelo menos um escopo para sincronizar.",
        icon: "tim-icons icon-alert-circle-exc",
      });
      return;
    }

    try {
      setSyncingNational(true);
      const result = await api.post(urlPostSyncHolidays, {
        idEmbarcador: empresaId,
        ano: parsedYear,
        escopos: nationalSyncScopes,
      });
      const payload = result?.data || {};
      const scopeResults = Array.isArray(payload.scopeResults) ? payload.scopeResults : [];
      const scopeErrors = Array.isArray(payload.scopeErrors) ? payload.scopeErrors : [];
      const hasSkipped = Array.isArray(payload.skipped) && payload.skipped.length > 0;
      const compRender = (
        <div style={{ textAlign: "left" }}>
          <p style={{ marginBottom: scopeResults.length > 0 || scopeErrors.length > 0 || hasSkipped ? 6 : 0 }}>
            {payload.message || `Feriados de ${parsedYear} sincronizados.`}
          </p>
          <p style={{ marginBottom: scopeResults.length > 0 || scopeErrors.length > 0 || hasSkipped ? 6 : 0 }}>
            {`${payload.createdCount || 0} criado(s), ${payload.updatedCount || 0} atualizado(s)`}
          </p>
          {scopeResults.length > 0 ? (
            <ul style={{ marginBottom: scopeErrors.length > 0 || hasSkipped ? 6 : 0, paddingLeft: 18 }}>
              {scopeResults.map((item) => {
                const scopeMeta = SYNC_SCOPE_OPTIONS.find((scope) => scope.value === item.scope);
                return (
                  <li key={item.scope}>
                    {item.error
                      ? `${scopeMeta?.label || item.scope}: falhou`
                      : `${scopeMeta?.label || item.scope}: ${item.importedCount || 0} sincronizado(s), ${
                          item.skippedCount || 0
                        } conflito(s)`}
                  </li>
                );
              })}
            </ul>
          ) : null}
          {scopeErrors.length > 0 ? (
            <ul style={{ marginBottom: hasSkipped ? 6 : 0, paddingLeft: 18 }}>
              {scopeErrors.map((item) => {
                const scopeMeta = SYNC_SCOPE_OPTIONS.find((scope) => scope.value === item.scope);
                return (
                  <li key={`error-${item.scope}`}>
                    {`${scopeMeta?.label || item.scope}: ${item.message}`}
                  </li>
                );
              })}
            </ul>
          ) : null}
          {hasSkipped ? (
            <ul style={{ marginBottom: 0, paddingLeft: 18 }}>
              {payload.skipped.slice(0, 5).map((item) => (
                <li key={`${item.escopo || "scope"}-${item.data}-${item.nome}`}>
                  {`${item.data}: ${item.nome}`}
                </li>
              ))}
              {payload.skipped.length > 5 ? (
                <li>{`+${payload.skipped.length - 5} conflito(s) adicional(is)`}</li>
              ) : null}
            </ul>
          ) : null}
        </div>
      );

      addToast({
        place: "tr",
        type: scopeErrors.length > 0 ? "warning" : "success",
        title: scopeErrors.length > 0 ? `Sincronizacao parcial ${parsedYear}` : `Sincronizacao ${parsedYear}`,
        description: compRender,
        icon: "tim-icons icon-check-2",
      });
      setNationalSyncModalOpen(false);
      setYearFilter(String(parsedYear));
      loadData();
    } catch (error) {
      addToast({
        place: "tr",
        type: "danger",
        title: "Falha na sincronizacao",
        description:
          error?.response?.data?.error ||
          "Nao foi possivel sincronizar os feriados selecionados no provedor configurado.",
        icon: "tim-icons icon-alert-circle-exc",
      });
    } finally {
      setSyncingNational(false);
    }
  }, [addToast, empresaId, loadData, nationalSyncScopes, nationalSyncYear]);

  const clearFilters = useCallback(() => {
    setSearchTerm("");
    setYearFilter(ALL_FILTER);
    setTypeFilter(ALL_FILTER);
    setStateFilter(ALL_FILTER);
  }, []);

  const normalizedData = useMemo(() => {
    return (Array.isArray(data) ? data : [])
      .map((item, index) => normalizeHoliday(item, index, today))
      .sort(compareByOperationalRelevance);
  }, [data, today]);

  const yearOptions = useMemo(() => {
    return [...new Set(normalizedData.map((item) => item.year).filter((year) => year !== "Sem data"))]
      .sort((a, b) => Number(b) - Number(a));
  }, [normalizedData]);

  const yearCounts = useMemo(() => {
    return normalizedData.reduce((acc, item) => {
      if (item.year === "Sem data") {
        return acc;
      }

      acc[item.year] = (acc[item.year] || 0) + 1;
      return acc;
    }, {});
  }, [normalizedData]);

  const latestAvailableYear = yearOptions.length > 0 ? yearOptions[0] : null;

  const yearSelectOptions = useMemo(() => {
    const nextOptions = new Set(yearOptions);

    if (yearFilter !== ALL_FILTER) {
      nextOptions.add(yearFilter);
    }

    return [...nextOptions].sort((a, b) => Number(b) - Number(a));
  }, [yearFilter, yearOptions]);

  const typeOptions = useMemo(() => {
    const seen = new Set();
    const options = [];

    normalizedData.forEach((item) => {
      if (seen.has(item.typeMeta.value)) {
        return;
      }

      seen.add(item.typeMeta.value);
      options.push(item.typeMeta);
    });

    return options;
  }, [normalizedData]);

  const stateOptions = useMemo(() => {
    return [...new Set(normalizedData.map((item) => item.state).filter(Boolean))].sort((a, b) =>
      a.localeCompare(b, "pt-BR")
    );
  }, [normalizedData]);

  const toolbarFilteredHolidays = useMemo(() => {
    const query = normalizeText(debouncedSearch);

    return normalizedData.filter((item) => {
      if (yearFilter !== ALL_FILTER && item.year !== yearFilter) {
        return false;
      }

      if (typeFilter !== ALL_FILTER && item.typeMeta.value !== typeFilter) {
        return false;
      }

      if (stateFilter !== ALL_FILTER && item.state !== stateFilter) {
        return false;
      }

      if (query && !item.searchIndex.includes(query)) {
        return false;
      }

      return true;
    });
  }, [debouncedSearch, normalizedData, stateFilter, typeFilter, yearFilter]);

  const viewCounts = useMemo(() => {
    const upcomingCount = toolbarFilteredHolidays.filter(
      (item) => item.distanceInDays !== null && item.distanceInDays >= 0
    ).length;
    const pastCount = toolbarFilteredHolidays.filter(
      (item) => item.distanceInDays !== null && item.distanceInDays < 0
    ).length;
    const activeCount = toolbarFilteredHolidays.filter((item) => item.statusMeta.isActive).length;
    const inactiveCount = toolbarFilteredHolidays.filter((item) => !item.statusMeta.isActive).length;

    return {
      upcoming: upcomingCount,
      past: pastCount,
      active: activeCount,
      inactive: inactiveCount,
    };
  }, [toolbarFilteredHolidays]);

  const focusedHolidays = useMemo(() => {
    switch (viewFilter) {
      case VIEW_META.past.value:
        return toolbarFilteredHolidays.filter(
          (item) => item.distanceInDays !== null && item.distanceInDays < 0
        );
      case VIEW_META.active.value:
        return toolbarFilteredHolidays.filter((item) => item.statusMeta.isActive);
      case VIEW_META.inactive.value:
        return toolbarFilteredHolidays.filter((item) => !item.statusMeta.isActive);
      case VIEW_META.upcoming.value:
      default:
        return toolbarFilteredHolidays.filter(
          (item) => item.distanceInDays !== null && item.distanceInDays >= 0
        );
    }
  }, [toolbarFilteredHolidays, viewFilter]);

  const hasFiltersApplied =
    normalizeText(searchTerm) !== "" ||
    yearFilter !== ALL_FILTER ||
    typeFilter !== ALL_FILTER ||
    stateFilter !== ALL_FILTER;
  const activeFiltersCount = [yearFilter, typeFilter, stateFilter].filter(
    (value) => value !== ALL_FILTER
  ).length;

  const yearFilterOptions = useMemo(
    () => [
      { value: ALL_FILTER, label: "Todos os anos" },
      ...yearSelectOptions.map((year) => ({ value: year, label: year })),
    ],
    [yearSelectOptions]
  );

  const typeFilterOptions = useMemo(
    () => [
      { value: ALL_FILTER, label: "Todos os tipos" },
      ...typeOptions.map((option) => ({ value: option.value, label: option.label })),
    ],
    [typeOptions]
  );

  const stateFilterOptions = useMemo(
    () => [
      { value: ALL_FILTER, label: "Todas as UFs" },
      ...stateOptions.map((state) => ({ value: state, label: state })),
    ],
    [stateOptions]
  );

  const scopedHolidays = hasFiltersApplied ? toolbarFilteredHolidays : normalizedData;
  const activeViewMeta = VIEW_META[viewFilter] || VIEW_META.upcoming;

  const groupedHolidays = useMemo(() => {
    const groups = [];
    const groupMap = new Map();

    focusedHolidays.forEach((item) => {
      if (!groupMap.has(item.monthKey)) {
        const nextGroup = {
          key: item.monthKey,
          label: item.monthLabel,
          items: [],
        };
        groupMap.set(item.monthKey, nextGroup);
        groups.push(nextGroup);
      }

      groupMap.get(item.monthKey).items.push(item);
    });

    return groups;
  }, [focusedHolidays]);

  const upcomingHolidays = useMemo(() => {
    return scopedHolidays
      .filter((item) => item.statusMeta.isActive && item.distanceInDays !== null && item.distanceInDays >= 0)
      .sort((a, b) => a.sortTime - b.sortTime)
      .slice(0, 6);
  }, [scopedHolidays]);

  const coverageByState = useMemo(() => {
    const counts = scopedHolidays.reduce((acc, item) => {
      if (!item.state) {
        return acc;
      }

      acc[item.state] = (acc[item.state] || 0) + 1;
      return acc;
    }, {});

    return Object.entries(counts)
      .map(([state, count]) => ({ state, count }))
      .sort((a, b) => b.count - a.count || a.state.localeCompare(b.state, "pt-BR"))
      .slice(0, 6);
  }, [scopedHolidays]);

  const selectedYearCount = yearFilter !== ALL_FILTER ? yearCounts[yearFilter] || 0 : normalizedData.length;
  const suggestedYear =
    yearFilter !== ALL_FILTER &&
    selectedYearCount === 0 &&
    latestAvailableYear &&
    latestAvailableYear !== yearFilter
      ? latestAvailableYear
      : null;
  const isInitialLoading = loading && normalizedData.length === 0;

  useEffect(() => {
    if (!HOLIDAYS_DEBUG) {
      return;
    }

    console.log("[Holidays] Date normalization", {
      browserNow: new Date().toISOString(),
      todayReference: today.toISOString(),
      currentYear,
      previousYear,
      nextYear,
      rawTotal: Array.isArray(data) ? data.length : 0,
      normalizedTotal: normalizedData.length,
      yearOptions,
      yearCounts,
      sample: normalizedData.slice(0, 12).map((item) => ({
        id: item.id,
        rawData: item.raw?.data || item.raw?.Data,
        parsedDate: item.parsedDate ? item.parsedDate.toISOString() : null,
        derivedYear: item.year,
        name: item.name,
      })),
    });
  }, [
    currentYear,
    data,
    nextYear,
    normalizedData,
    previousYear,
    today,
    yearCounts,
    yearOptions,
  ]);

  useEffect(() => {
    if (!HOLIDAYS_DEBUG) {
      return;
    }

    console.log("[Holidays] Filter state", {
      yearFilter,
      typeFilter,
      stateFilter,
      viewFilter,
      searchTerm,
      debouncedSearch,
      toolbarFilteredTotal: toolbarFilteredHolidays.length,
      focusedTotal: focusedHolidays.length,
      groupedMonths: groupedHolidays.map((group) => ({
        key: group.key,
        label: group.label,
        total: group.items.length,
      })),
      filteredSample: focusedHolidays.slice(0, 12).map((item) => ({
        id: item.id,
        rawData: item.raw?.data || item.raw?.Data,
        derivedYear: item.year,
        name: item.name,
      })),
    });
  }, [
    debouncedSearch,
    focusedHolidays,
    groupedHolidays,
    searchTerm,
    stateFilter,
    typeFilter,
    toolbarFilteredHolidays,
    viewFilter,
    yearFilter,
  ]);

  if (!hasEmpresaSelection) {
    return (
      <div className="content">
        <SpecificEmpresaRequiredNotice />
      </div>
    );
  }

  return (
    <div className="content">
      <Modal
        isOpen={isManualModalOpen}
        toggle={closeManualModal}
        size="lg"
        className="holidays-manual-modal"
      >
        <ModalHeader toggle={closeManualModal}>Cadastrar feriado manualmente</ModalHeader>
        <ModalBody>
          <div className="holidays-manual-modal__intro">
            <h4>Cadastro pontual de feriado</h4>
            <p>
              Use este fluxo para excecoes, feriados estaduais ou municipais e ajustes que nao
              dependem da sincronizacao nacional ou de importacao por CSV.
            </p>
          </div>

          <Row>
            <Col md="7">
              <FormGroup>
                <Label className="holidays-field-label" for="holiday-manual-name">
                  Nome
                </Label>
                <Input
                  id="holiday-manual-name"
                  value={manualHolidayForm.nome}
                  onChange={(event) => updateManualHolidayForm("nome", event.target.value)}
                  invalid={Boolean(manualHolidayErrors.nome)}
                  placeholder="Ex.: Revolucao Constitucionalista"
                />
                {manualHolidayErrors.nome ? (
                  <FormFeedback>{manualHolidayErrors.nome}</FormFeedback>
                ) : null}
              </FormGroup>
            </Col>
            <Col md="5">
              <FormGroup>
                <Label className="holidays-field-label" for="holiday-manual-date">
                  Data
                </Label>
                <Input
                  id="holiday-manual-date"
                  type="date"
                  value={manualHolidayForm.data}
                  onChange={(event) => updateManualHolidayForm("data", event.target.value)}
                  invalid={Boolean(manualHolidayErrors.data)}
                />
                {manualHolidayErrors.data ? (
                  <FormFeedback>{manualHolidayErrors.data}</FormFeedback>
                ) : null}
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md="4">
              <FormGroup>
                <Label className="holidays-field-label" for="holiday-manual-type">
                  Tipo
                </Label>
                <Input
                  id="holiday-manual-type"
                  type="select"
                  value={manualHolidayForm.tipo}
                  onChange={(event) => updateManualHolidayForm("tipo", event.target.value)}
                >
                  <option value={TYPE_META.nacional.value}>Nacional</option>
                  <option value={TYPE_META.estadual.value}>Estadual</option>
                  <option value={TYPE_META.municipal.value}>Municipal</option>
                </Input>
              </FormGroup>
            </Col>
            <Col md="4">
              <FormGroup>
                <Label className="holidays-field-label" for="holiday-manual-status">
                  Status
                </Label>
                <Input
                  id="holiday-manual-status"
                  type="select"
                  value={manualHolidayForm.status}
                  onChange={(event) => updateManualHolidayForm("status", event.target.value)}
                >
                  <option value="active">Ativo</option>
                  <option value="inactive">Inativo</option>
                </Input>
              </FormGroup>
            </Col>
            {manualTypeRequiresState ? (
              <Col md="4">
                <FormGroup>
                  <Label className="holidays-field-label" for="holiday-manual-uf">
                    UF
                  </Label>
                  <Input
                    id="holiday-manual-uf"
                    type="select"
                    value={manualHolidayForm.uf}
                    onChange={(event) => updateManualHolidayForm("uf", event.target.value)}
                    invalid={Boolean(manualHolidayErrors.uf)}
                    disabled={loadingManualStates}
                  >
                    <option value="">{loadingManualStates ? "Carregando UFs..." : "Selecione"}</option>
                    {manualStateOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Input>
                  {manualHolidayErrors.uf ? (
                    <FormFeedback>{manualHolidayErrors.uf}</FormFeedback>
                  ) : null}
                </FormGroup>
              </Col>
            ) : null}
          </Row>

          {manualTypeRequiresCity ? (
            <Row>
              <Col md="12">
                <FormGroup>
                  <Label className="holidays-field-label" for="holiday-manual-city">
                    Cidade
                  </Label>
                  <AsyncSelect
                    inputId="holiday-manual-city"
                    classNamePrefix="wizard-select"
                    cacheOptions
                    defaultOptions
                    loadOptions={loadManualCityOptions}
                    value={manualHolidayForm.cidade}
                    onChange={(option) => updateManualHolidayForm("cidade", option)}
                    placeholder={
                      manualHolidayForm.uf
                        ? "Buscar cidade da UF selecionada"
                        : "Selecione a UF antes de buscar a cidade"
                    }
                    isDisabled={!manualHolidayForm.uf}
                    noOptionsMessage={() =>
                      manualHolidayForm.uf ? "Nenhuma cidade encontrada" : "Selecione uma UF"
                    }
                  />
                  {manualHolidayErrors.idCidade ? (
                    <FormFeedback style={{ display: "block" }}>
                      {manualHolidayErrors.idCidade}
                    </FormFeedback>
                  ) : null}
                </FormGroup>
              </Col>
            </Row>
          ) : null}

          <FormGroup>
            <Label className="holidays-field-label" for="holiday-manual-description">
              Descricao
            </Label>
            <Input
              id="holiday-manual-description"
              type="textarea"
              rows="4"
              value={manualHolidayForm.descricao}
              onChange={(event) => updateManualHolidayForm("descricao", event.target.value)}
              placeholder="Contexto operacional, abrangencia ou observacao interna"
            />
          </FormGroup>

          <div className="holidays-manual-modal__note">
            Feriados nacionais ignoram UF e cidade. Feriados estaduais exigem UF. Feriados
            municipais exigem UF e cidade.
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            color="default"
            outline
            className="btn-round"
            onClick={closeManualModal}
            disabled={savingManualHoliday}
          >
            Fechar
          </Button>
          <Button
            color="info"
            className="btn-round"
            onClick={handleCreateManualHoliday}
            disabled={savingManualHoliday}
          >
            {savingManualHoliday ? "Salvando..." : "Cadastrar feriado"}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={isImportModalOpen}
        toggle={() => setImportModalOpen((current) => !current)}
        size="lg"
        className="holidays-import-modal"
      >
        <ModalHeader toggle={() => setImportModalOpen(false)}>Atualizar base via CSV</ModalHeader>
        <ModalBody>
          <div className="holidays-import-modal__intro">
            <h4>Fluxo recomendado para manutencao em lote</h4>
            <p>
              Esta tela trabalha com exportacao e importacao de arquivo. Use a base atual como
              referencia, padronize o calendario e reenviar o CSV somente depois de revisar
              duplicidades e abrangencia territorial.
            </p>
          </div>
          <div className="holidays-import-modal__tips">
            <div className="holidays-import-tip">
              <strong>1. Exporte a base vigente</strong>
              <span>Baixe o CSV atual para preservar o layout e revisar o calendario existente.</span>
            </div>
            <div className="holidays-import-tip">
              <strong>2. Ajuste datas e territorio</strong>
              <span>Revise tipo, UF, cidade, nome e status antes de preparar o novo arquivo.</span>
            </div>
            <div className="holidays-import-tip">
              <strong>3. Reimporte para recarregar a tela</strong>
              <span>A listagem e os indicadores desta pagina sao atualizados logo apos o envio.</span>
            </div>
          </div>
          <div className="holidays-import-modal__upload">
            <FileUpload
              api={api}
              url={urlPostImportCsv}
              params={{ idEmbarcador: empresaId }}
              paramsFormData={null}
              accept=".csv"
              filekey="file"
              onSuccess={handleImportSuccess}
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <span className="holidays-import-modal__note">
            Use o CSV exportado pela propria tela como ponto de partida sempre que possivel.
          </span>
          <Button color="default" outline className="btn-round" onClick={() => setImportModalOpen(false)}>
            Fechar
          </Button>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={isNationalSyncModalOpen}
        toggle={() => setNationalSyncModalOpen((current) => !current)}
        className="holidays-national-modal"
      >
        <ModalHeader toggle={() => setNationalSyncModalOpen(false)}>
          Sincronizar feriados
        </ModalHeader>
        <ModalBody>
          <div className="holidays-national-modal__intro">
            <h4>Base automatica por escopo</h4>
            <p>
              Escolha quais escopos deseja sincronizar no ano selecionado. Para estaduais e
              capitais, a busca usa a cobertura automatica da Feriados API.
            </p>
          </div>
          <Label className="holidays-field-label">Escopos da sincronizacao</Label>
          <div className="holidays-sync-scope-grid">
            {SYNC_SCOPE_OPTIONS.map((scope) => {
              const active = nationalSyncScopes.includes(scope.value);

              return (
                <button
                  key={scope.value}
                  type="button"
                  className={`holidays-sync-scope-card ${
                    active ? "holidays-sync-scope-card--active" : ""
                  }`}
                  onClick={() => toggleNationalSyncScope(scope.value)}
                >
                  <strong>{scope.label}</strong>
                  <span>{scope.helper}</span>
                </button>
              );
            })}
          </div>
          <div className="holidays-sync-scope-actions">
            <button
              type="button"
              className="holidays-sync-scope-action"
              onClick={() => setNationalSyncScopes([...DEFAULT_SYNC_SCOPES])}
            >
              Padrao operacional
            </button>
            <button
              type="button"
              className="holidays-sync-scope-action"
              onClick={() => setNationalSyncScopes(SYNC_SCOPE_OPTIONS.map((scope) => scope.value))}
            >
              Selecionar todos
            </button>
          </div>
          <FormGroup>
            <Label className="holidays-field-label" for="holiday-national-year">
              Ano para sincronizacao
            </Label>
            <Input
              id="holiday-national-year"
              type="number"
              min="2000"
              max="2100"
              value={nationalSyncYear}
              onChange={(event) => setNationalSyncYear(event.target.value)}
            />
          </FormGroup>
          <div className="holidays-national-modal__quick-years">
            {[previousYear, currentYear, nextYear].map((year) => (
              <button
                key={year}
                type="button"
                className={`holidays-quick-filter ${
                  nationalSyncYear === year ? "holidays-quick-filter--active" : ""
                }`}
                onClick={() => setNationalSyncYear(year)}
              >
                {year}
              </button>
            ))}
          </div>
          <div className="holidays-national-modal__note">
            O escopo estadual usa a UF do cadastro do embarcador. Para feriados municipais fora
            das capitais, o cadastro deve ser feito manualmente ou via CSV.
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            color="default"
            outline
            className="btn-round"
            onClick={() => setNationalSyncModalOpen(false)}
            disabled={syncingNational}
          >
            Fechar
          </Button>
          <Button
            color="info"
            className="btn-round"
            onClick={handleSyncNationalHolidays}
            disabled={syncingNational}
          >
            {syncingNational ? "Sincronizando..." : "Sincronizar feriados"}
          </Button>
        </ModalFooter>
      </Modal>

      <PageHero
        eyebrow="Calendario"
        title="Feriados do embarcador"
        description="Revise a base de feriados, filtre impactos operacionais e mantenha o calendario atualizado sem sair do embarcador selecionado."
        actions={
          <>
            <PageHeroRefreshButton onClick={loadData} loading={loading} />
            <PageHeroExportButton onClick={handleDownload} loading={downloading} />
            <Button
              className="btn-sm"
              color="secondary"
              onClick={() => setImportModalOpen(true)}
              disabled={loading}
            >
              Importar CSV
            </Button>
            <Button
              className="btn-sm"
              color="info"
              onClick={handleOpenNationalSyncModal}
              disabled={loading || syncingNational}
            >
              Sincronizar feriados
            </Button>
            <Button
              className="btn-sm"
              color="primary"
              onClick={openManualModal}
              disabled={loading || savingManualHoliday}
            >
              Cadastrar feriado
            </Button>
          </>
        }
      >
        {loadError ? <div style={loadErrorStyle}>{loadError}</div> : null}
      </PageHero>

      <FilterBand
        mode="client"
        searchLabel="Buscar feriado"
        searchHint="Nome, descricao, cidade, UF ou tipo"
        searchPlaceholder="Buscar por nome, descricao, cidade, UF ou tipo"
        searchValue={searchTerm}
        onSearchChange={setSearchTerm}
        activeFiltersCount={activeFiltersCount}
        open={filtersOpen}
        onToggle={setFiltersOpen}
        panelTitle="Filtros"
        panelDescription="Ano, tipo e UF do feriado."
        onClear={hasFiltersApplied ? clearFilters : undefined}
        showActions
      >
        <Row>
          <Col md={4}>
            <div style={fieldLabelStyle}>Ano</div>
            <Select
              inputId="holiday-year"
              classNamePrefix="react-select"
              styles={reactSelectStyles}
              {...reactSelectPortalProps()}
              options={yearFilterOptions}
              value={findOption(yearFilterOptions, yearFilter)}
              onChange={(option) => setYearFilter((option && option.value) || ALL_FILTER)}
              isSearchable={false}
            />
          </Col>
          <Col md={4}>
            <div style={fieldLabelStyle}>Tipo</div>
            <Select
              inputId="holiday-type"
              classNamePrefix="react-select"
              styles={reactSelectStyles}
              {...reactSelectPortalProps()}
              options={typeFilterOptions}
              value={findOption(typeFilterOptions, typeFilter)}
              onChange={(option) => setTypeFilter((option && option.value) || ALL_FILTER)}
              isSearchable={false}
            />
          </Col>
          <Col md={4}>
            <div style={fieldLabelStyle}>UF</div>
            <Select
              inputId="holiday-state"
              classNamePrefix="react-select"
              styles={reactSelectStyles}
              {...reactSelectPortalProps()}
              options={stateFilterOptions}
              value={findOption(stateFilterOptions, stateFilter)}
              onChange={(option) => setStateFilter((option && option.value) || ALL_FILTER)}
            />
          </Col>
        </Row>
      </FilterBand>

      <MetricCardGroup
        title="Visao do calendario"
        description="Clique em um indicador para trocar a visao da agenda."
      >
        {Object.values(VIEW_META).map((view) => (
          <MetricCard
            key={view.value}
            label={view.label}
            value={viewCounts[view.value]}
            helper={view.helper}
            tone={view.tone}
            iconClassName={view.iconClassName}
            active={viewFilter === view.value}
            onClick={() => setViewFilter(view.value)}
          />
        ))}
      </MetricCardGroup>

      <Row>
        <Col xl="8">
          <ShellCard
            eyebrow="Agenda"
            title={activeViewMeta.title}
            action={<MetaPill tone={activeViewMeta.tone}>{focusedHolidays.length}</MetaPill>}
          >
            {isInitialLoading ? (
              <div className="holidays-loader">
                <ClipLoader size={72} color="var(--theme-accent)" loading={loading} />
                <h5>Carregando calendario...</h5>
              </div>
            ) : groupedHolidays.length > 0 ? (
              <div className="holidays-months">
                {groupedHolidays.map((group) => (
                  <section key={group.key} className="holidays-month">
                    <div className="holidays-month__header">
                      <div>
                        <strong>{group.label}</strong>
                        <span>{group.items.length} feriado(s) neste bloco</span>
                      </div>
                    </div>
                    <div className="holidays-month__list">
                      {group.items.map((item) => (
                        <article key={item.id} className="holidays-entry">
                          <div className="holidays-entry__date">
                            <strong>{item.dayLabel}</strong>
                            <span>{item.monthShortLabel}</span>
                          </div>
                          <div className="holidays-entry__body">
                            <div className="holidays-entry__meta">
                              <MetaPill tone={item.typeMeta.tone}>{item.typeMeta.label}</MetaPill>
                              <MetaPill tone={item.statusMeta.tone}>{item.statusMeta.label}</MetaPill>
                            </div>
                            <h5 className="holidays-entry__title">{item.name}</h5>
                            {item.description ? (
                              <p className="holidays-entry__description">{item.description}</p>
                            ) : null}
                            <div className="holidays-entry__footer">
                              <span className="holidays-entry__location">
                                <FaMapMarkedAlt />
                                {"  "}
                                {item.locationLabel}
                              </span>
                              <span className="holidays-entry__date-label">{item.dateLabel}</span>
                            </div>
                          </div>
                          <div className="holidays-entry__side">
                            <span className="holidays-entry__relative">{item.relativeLabel}</span>
                          </div>
                        </article>
                      ))}
                    </div>
                  </section>
                ))}
              </div>
            ) : (
              <div className="holidays-empty-state">
                <h5>{`Nenhum feriado encontrado para a visao "${activeViewMeta.label}".`}</h5>
                <p>
                  {suggestedYear
                    ? `Nao ha feriados visiveis para ${yearFilter}. O ano mais recente disponivel nesta base e ${suggestedYear}.`
                    : "Ajuste os filtros ou troque a visao ativa para revisar o calendario do embarcador selecionado."}
                </p>
                <div className="holidays-empty-state__actions">
                  {suggestedYear ? (
                    <Button
                      color="primary"
                      outline
                      className="btn-round"
                      onClick={() => setYearFilter(suggestedYear)}
                    >
                      {`Ir para ${suggestedYear}`}
                    </Button>
                  ) : null}
                  {hasFiltersApplied ? (
                    <Button color="default" outline className="btn-round" onClick={clearFilters}>
                      Limpar filtros
                    </Button>
                  ) : null}
                  <Button color="primary" className="btn-round" onClick={handleOpenNationalSyncModal}>
                    Sincronizar feriados
                  </Button>
                  <Button color="info" className="btn-round" onClick={() => setImportModalOpen(true)}>
                    Importar CSV
                  </Button>
                </div>
              </div>
            )}
          </ShellCard>
        </Col>

        <Col xl="4">
          <div className="holidays-aside">
            <ShellCard
              eyebrow="Monitoramento"
              title="Proximos feriados"
              action={
                <MetaPill tone="success">
                  <FaRegClock />
                  {"  "}
                  {upcomingHolidays.length}
                </MetaPill>
              }
            >
              {upcomingHolidays.length > 0 ? (
                <div className="holidays-side-list">
                  {upcomingHolidays.map((item) => (
                    <div key={`upcoming-${item.id}`} className="holidays-side-list__item">
                      <div>
                        <strong>{item.name}</strong>
                        <span>
                          {item.dateLabel} • {item.locationLabel}
                        </span>
                      </div>
                      <MetaPill tone="info">{item.relativeLabel}</MetaPill>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="holidays-side-empty">
                  Nenhum feriado ativo futuro para o recorte aplicado.
                </p>
              )}
            </ShellCard>

            <ShellCard
              eyebrow="Cobertura"
              title="Territorio em destaque"
              action={<MetaPill tone="muted">{coverageByState.length} UFs</MetaPill>}
              style={{ marginTop: 0 }}
            >
              {coverageByState.length > 0 ? (
                <div className="holidays-coverage-list">
                  {coverageByState.map((item) => (
                    <div key={item.state} className="holidays-coverage-list__item">
                      <strong>{item.state}</strong>
                      <span>{item.count} feriado(s)</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="holidays-side-empty">
                  Nenhuma UF aparece no recorte atual. Verifique se ainda nao ha feriados
                  estaduais ou municipais visiveis nesta base.
                </p>
              )}
            </ShellCard>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default Holidays;
